import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPaymentLinkSettings, updatePaymentLinkSettings } from '@/api/client';

interface PaymentLinkSettingsData {
  enabled: boolean;
  provider: string;
  environment: string;
  defaultExpirationDays: number;
  allowPartialPayment: boolean;
  sendReceiptAfterPayment: boolean;
  autoReceiptType: number | null;
  autoReceiptPaymentType: number | null;
  autoReceiptSendMail: boolean;
  isConfigured: boolean;
}

export default function PaymentLinkSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<PaymentLinkSettingsData>({
    enabled: false,
    provider: 'icredit',
    environment: 'sandbox',
    defaultExpirationDays: 30,
    allowPartialPayment: false,
    sendReceiptAfterPayment: false,
    autoReceiptType: null,
    autoReceiptPaymentType: null,
    autoReceiptSendMail: false,
    isConfigured: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Credential fields (not loaded from server, only for saving)
  const [apiKey, setApiKey] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [terminalId, setTerminalId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [baseUrl, setBaseUrl] = useState('');

  useEffect(() => {
    getPaymentLinkSettings()
      .then((data) => {
        setSettings({
          enabled: data.enabled ?? false,
          provider: data.provider ?? 'icredit',
          environment: data.environment ?? 'sandbox',
          defaultExpirationDays: data.defaultExpirationDays ?? 30,
          allowPartialPayment: data.allowPartialPayment ?? false,
          sendReceiptAfterPayment: data.sendReceiptAfterPayment ?? false,
          autoReceiptType: data.autoReceiptType ?? null,
          autoReceiptPaymentType: data.autoReceiptPaymentType ?? null,
          autoReceiptSendMail: data.autoReceiptSendMail ?? false,
          isConfigured: data.isConfigured ?? false,
        });
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, []);

  const clearCredentials = () => {
    setApiKey('');
    setApiKeyId('');
    setApiSecret('');
    setBusinessId('');
    setTerminalId('');
    setCompanyId('');
    setUsername('');
    setPassword('');
    setLoginUrl('');
    setBaseUrl('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        enabled: settings.enabled,
        provider: settings.provider,
        environment: settings.environment,
        defaultExpirationDays: settings.defaultExpirationDays,
        allowPartialPayment: settings.allowPartialPayment,
        sendReceiptAfterPayment: settings.sendReceiptAfterPayment,
        autoReceiptType: settings.autoReceiptType,
        autoReceiptPaymentType: settings.autoReceiptPaymentType,
        autoReceiptSendMail: settings.autoReceiptSendMail,
      };
      if (apiKey || apiKeyId || apiSecret || businessId || terminalId || companyId || username || password || loginUrl || baseUrl) {
        payload.credentials = { apiKey, apiKeyId, apiSecret, businessId, terminalId, companyId, username, password, loginUrl, baseUrl };
      }
      const saved = await updatePaymentLinkSettings(payload);
      setSettings(current => ({ ...current, isConfigured: saved.isConfigured === true }));
      clearCredentials();
      setMessage({ type: 'success', text: t('settings.saved', 'Settings saved') });
    } catch {
      setMessage({ type: 'error', text: t('settings.saveFailed', 'Failed to save settings') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-400">{t('common.loading', 'Loading...')}</p>;
  if (loadFailed) return <p role="alert" className="text-sm text-red-500">{t('common.error')}</p>;

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
        />
        <span className="text-sm text-gray-700">
          {t('paymentLinks.enabled', 'Enable Payment Links')}
        </span>
      </label>

      {settings.enabled && (
        <div className="space-y-4 bg-gray-50 rounded-lg p-4">
          {/* Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('paymentLinks.provider', 'Payment Provider')}
            </label>
            <select
              value={settings.provider}
              onChange={(e) => {
                clearCredentials();
                setSettings({ ...settings, provider: e.target.value, isConfigured: false });
              }}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="icredit">iCredit</option>
              <option value="green_invoice">Green Invoice</option>
              <option value="pelecard">pELECARD</option>
            </select>
          </div>

          {/* Environment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('paymentLinks.environment', 'Environment')}
            </label>
            <select
              value={settings.environment}
              onChange={(e) => setSettings({ ...settings, environment: e.target.value })}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>

          {/* Expiration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('paymentLinks.defaultExpiration', 'Default Expiration (days)')}
            </label>
            <input
              type="number"
              min={1}
              max={365}
              value={settings.defaultExpirationDays}
              onChange={(e) => setSettings({ ...settings, defaultExpirationDays: parseInt(e.target.value) || 30 })}
              className="w-32 rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          {/* Checkboxes */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.allowPartialPayment}
              onChange={(e) => setSettings({ ...settings, allowPartialPayment: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              {t('paymentLinks.allowPartial', 'Allow Partial Payment')}
            </span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.sendReceiptAfterPayment}
              onChange={(e) => setSettings({ ...settings, sendReceiptAfterPayment: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              {t('paymentLinks.sendReceipt', 'Send Receipt After Payment')}
            </span>
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('paymentLinks.autoReceiptType', 'Rivhit receipt type')}
              </label>
              <input
                type="number"
                min={1}
                value={settings.autoReceiptType ?? ''}
                onChange={(e) => setSettings({
                  ...settings,
                  autoReceiptType: e.target.value ? Number(e.target.value) : null,
                })}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('paymentLinks.autoReceiptPaymentType', 'Credit-card payment type')}
              </label>
              <input
                type="number"
                min={1}
                value={settings.autoReceiptPaymentType ?? ''}
                onChange={(e) => setSettings({
                  ...settings,
                  autoReceiptPaymentType: e.target.value ? Number(e.target.value) : null,
                })}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={settings.autoReceiptSendMail}
                onChange={(e) => setSettings({ ...settings, autoReceiptSendMail: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                {t('paymentLinks.autoReceiptSendMail', 'Email automatic receipts')}
              </span>
            </label>
          </div>

          {/* Credentials */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {t('paymentLinks.credentials', 'Provider Credentials')}
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              {t('paymentLinks.credentialsNote', 'Credentials are stored securely and never displayed.')}
              {settings.isConfigured && (
                <span className="text-green-600 ltr:ml-1 rtl:mr-1">
                  ✅ {t('paymentLinks.configured', 'Configured')}
                </span>
              )}
            </p>

            <div className="space-y-2">
              {settings.provider === 'green_invoice' ? (
                <>
                  <input
                    type="text"
                    placeholder="Green Invoice API key id"
                    value={apiKeyId}
                    onChange={(e) => setApiKeyId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="password"
                    placeholder="Green Invoice API key secret"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Business ID (optional)"
                    value={businessId}
                    onChange={(e) => setBusinessId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Green Invoice API base URL override"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </>
              ) : settings.provider === 'pelecard' ? (
                <>
                  <input
                    type="text"
                    aria-label={t('paymentLinks.terminalId', 'Terminal ID')}
                    placeholder={t('paymentLinks.terminalId', 'Terminal ID')}
                    value={terminalId}
                    onChange={(e) => setTerminalId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    aria-label={t('settings.rivhit.username', 'Username')}
                    placeholder={t('settings.rivhit.username', 'Username')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="password"
                    aria-label={t('settings.rivhit.password', 'Password')}
                    placeholder={t('settings.rivhit.password', 'Password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </>
              ) : (
                <>
                  <input
                    type="password"
                    placeholder={t('paymentLinks.apiKey', 'GroupPrivateToken')}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder={t('paymentLinks.terminalId', 'Terminal ID')}
                    value={terminalId}
                    onChange={(e) => setTerminalId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder={t('paymentLinks.companyId', 'Company ID')}
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder={t('paymentLinks.baseUrl', 'iCredit API base URL')}
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder={t('paymentLinks.loginUrl', 'iCredit login URL')}
                    value={loginUrl}
                    onChange={(e) => setLoginUrl(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder={t('paymentLinks.username', 'iCredit username')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="password"
                    placeholder={t('paymentLinks.password', 'iCredit password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </>
              )}
            </div>
          </div>

          {message && (
            <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
              {message.text}
            </p>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? '...' : t('settings.save', 'Save')}
          </button>
        </div>
      )}
    </div>
  );
}
